/**
 * XRechnung validation helpers
 * Validates invoice data before XML generation
 */

import {XRechnungInvoiceData} from './types';

/**
 * Validation error
 */
export interface ValidationError {
    field: string;
    message: string;
    code: string;
}

/**
 * Validation result
 */
export interface ValidationResult {
    valid: boolean;
    errors: ValidationError[];
    warnings: ValidationError[];
}

/**
 * Validate seller party data
 */
function validateSeller(invoice: XRechnungInvoiceData, errors: ValidationError[], warnings: ValidationError[]): void {
    const seller = invoice.seller;

    if (!seller.name) {
        errors.push({field: 'seller.name', message: 'Seller name is required (BT-27)', code: 'SELLER_NAME_REQUIRED'});
    }

    if (!seller.identification.vatId) {
        warnings.push({field: 'seller.identification.vatId', message: 'Seller VAT ID is recommended (BT-31)', code: 'SELLER_VAT_MISSING'});
    } else if (!seller.identification.vatId.startsWith('DE')) {
        warnings.push({field: 'seller.identification.vatId', message: 'Seller VAT ID should start with DE for German companies', code: 'SELLER_VAT_FORMAT'});
    }

    if (!seller.postalAddress.streetName) {
        errors.push({field: 'seller.postalAddress.streetName', message: 'Seller street is required (BT-35)', code: 'SELLER_STREET_REQUIRED'});
    }

    if (!seller.postalAddress.cityName) {
        errors.push({field: 'seller.postalAddress.cityName', message: 'Seller city is required (BT-37)', code: 'SELLER_CITY_REQUIRED'});
    }

    if (!seller.postalAddress.postalZone) {
        errors.push({field: 'seller.postalAddress.postalZone', message: 'Seller postal code is required (BT-38)', code: 'SELLER_POSTAL_REQUIRED'});
    }

    if (!seller.postalAddress.countryCode) {
        errors.push({field: 'seller.postalAddress.countryCode', message: 'Seller country code is required (BT-40)', code: 'SELLER_COUNTRY_REQUIRED'});
    }
}

/**
 * Validate buyer party data
 */
function validateBuyer(invoice: XRechnungInvoiceData, errors: ValidationError[], warnings: ValidationError[]): void {
    const buyer = invoice.buyer;

    if (!buyer.name) {
        errors.push({field: 'buyer.name', message: 'Buyer name is required (BT-44)', code: 'BUYER_NAME_REQUIRED'});
    }

    if (!buyer.postalAddress.streetName) {
        errors.push({field: 'buyer.postalAddress.streetName', message: 'Buyer street is required (BT-50)', code: 'BUYER_STREET_REQUIRED'});
    }

    if (!buyer.postalAddress.cityName) {
        errors.push({field: 'buyer.postalAddress.cityName', message: 'Buyer city is required (BT-52)', code: 'BUYER_CITY_REQUIRED'});
    }

    if (!buyer.postalAddress.postalZone) {
        errors.push({field: 'buyer.postalAddress.postalZone', message: 'Buyer postal code is required (BT-53)', code: 'BUYER_POSTAL_REQUIRED'});
    }

    if (!buyer.postalAddress.countryCode) {
        errors.push({field: 'buyer.postalAddress.countryCode', message: 'Buyer country code is required (BT-55)', code: 'BUYER_COUNTRY_REQUIRED'});
    }

    // B2G requires Leitweg-ID
    if (buyer.identification?.leitwegId) {
        // Validate Leitweg-ID format (simplified check)
        const leitwegPattern = /^\d{2,12}-\d{1,30}-\d{2}$/;
        if (!leitwegPattern.test(buyer.identification.leitwegId)) {
            warnings.push({
                field: 'buyer.identification.leitwegId',
                message: 'Leitweg-ID format may be invalid (expected: XX-XXXX-XX)',
                code: 'LEITWEG_FORMAT',
            });
        }
    }
}

/**
 * Validate payment data
 */
function validatePayment(invoice: XRechnungInvoiceData, errors: ValidationError[], warnings: ValidationError[]): void {
    const payment = invoice.paymentMeans;

    if (!payment.paymentMeansCode) {
        errors.push({field: 'paymentMeans.paymentMeansCode', message: 'Payment means code is required (BT-81)', code: 'PAYMENT_CODE_REQUIRED'});
    }

    if (!payment.iban) {
        errors.push({field: 'paymentMeans.iban', message: 'IBAN is required for SEPA transfer (BT-84)', code: 'IBAN_REQUIRED'});
    } else {
        // Basic IBAN validation (German IBANs are 22 characters)
        const ibanClean = payment.iban.replace(/\s/g, '');
        if (!ibanClean.startsWith('DE') || ibanClean.length !== 22) {
            warnings.push({field: 'paymentMeans.iban', message: 'IBAN format may be invalid for German account', code: 'IBAN_FORMAT'});
        }
    }
}

/**
 * Validate line items
 */
function validateLineItems(invoice: XRechnungInvoiceData, errors: ValidationError[], warnings: ValidationError[]): void {
    if (!invoice.lineItems || invoice.lineItems.length === 0) {
        errors.push({field: 'lineItems', message: 'At least one invoice line is required', code: 'LINE_ITEMS_REQUIRED'});
        return;
    }

    invoice.lineItems.forEach((item, index) => {
        const prefix = `lineItems[${index}]`;

        if (!item.id) {
            errors.push({field: `${prefix}.id`, message: `Line ${index + 1}: Line ID is required (BT-126)`, code: 'LINE_ID_REQUIRED'});
        }

        if (!item.description) {
            errors.push({field: `${prefix}.description`, message: `Line ${index + 1}: Description is required (BT-153)`, code: 'LINE_DESC_REQUIRED'});
        }

        if (item.quantity <= 0) {
            warnings.push({field: `${prefix}.quantity`, message: `Line ${index + 1}: Quantity should be positive`, code: 'LINE_QUANTITY_INVALID'});
        }

        if (!item.unitCode) {
            errors.push({field: `${prefix}.unitCode`, message: `Line ${index + 1}: Unit code is required (BT-130)`, code: 'LINE_UNIT_REQUIRED'});
        }

        if (item.unitPrice < 0) {
            warnings.push({field: `${prefix}.unitPrice`, message: `Line ${index + 1}: Unit price is negative`, code: 'LINE_PRICE_NEGATIVE'});
        }
    });
}

/**
 * Validate totals
 */
function validateTotals(invoice: XRechnungInvoiceData, errors: ValidationError[], warnings: ValidationError[]): void {
    // Calculate expected totals
    const expectedLineExtension = invoice.lineItems.reduce((sum, item) => sum + item.lineNetAmount, 0);
    const expectedTax = invoice.vatBreakdown.reduce((sum, vat) => sum + vat.taxAmount, 0);
    const expectedTotal = expectedLineExtension + expectedTax;

    // Check for rounding differences (allow 0.01 tolerance)
    const tolerance = 0.01;

    if (Math.abs(invoice.lineExtensionAmount - expectedLineExtension) > tolerance) {
        warnings.push({
            field: 'lineExtensionAmount',
            message: `Line extension amount (${invoice.lineExtensionAmount}) doesn't match sum of line totals (${expectedLineExtension})`,
            code: 'TOTAL_LINE_MISMATCH',
        });
    }

    if (Math.abs(invoice.taxInclusiveAmount - expectedTotal) > tolerance) {
        warnings.push({
            field: 'taxInclusiveAmount',
            message: `Tax inclusive amount (${invoice.taxInclusiveAmount}) doesn't match calculated total (${expectedTotal})`,
            code: 'TOTAL_MISMATCH',
        });
    }
}

/**
 * Validate document level fields
 */
function validateDocument(invoice: XRechnungInvoiceData, errors: ValidationError[], warnings: ValidationError[]): void {
    if (!invoice.invoiceNumber) {
        errors.push({field: 'invoiceNumber', message: 'Invoice number is required (BT-1)', code: 'INVOICE_NUMBER_REQUIRED'});
    }

    if (!invoice.invoiceDate) {
        errors.push({field: 'invoiceDate', message: 'Invoice date is required (BT-2)', code: 'INVOICE_DATE_REQUIRED'});
    }

    if (!invoice.invoiceTypeCode) {
        errors.push({field: 'invoiceTypeCode', message: 'Invoice type code is required (BT-3)', code: 'INVOICE_TYPE_REQUIRED'});
    }

    if (!invoice.currencyCode) {
        errors.push({field: 'currencyCode', message: 'Currency code is required (BT-5)', code: 'CURRENCY_REQUIRED'});
    }

    if (!invoice.specificationId) {
        errors.push({field: 'specificationId', message: 'Specification ID is required (BT-24)', code: 'SPEC_ID_REQUIRED'});
    }

    // XRechnung requires buyer reference for B2G
    if (!invoice.buyerReference) {
        warnings.push({field: 'buyerReference', message: 'Buyer reference is recommended (BT-10), required for B2G', code: 'BUYER_REF_MISSING'});
    }
}

/**
 * Validate XRechnung invoice data
 * @param invoice - Invoice data to validate
 * @returns Validation result with errors and warnings
 */
export function validateXRechnungData(invoice: XRechnungInvoiceData): ValidationResult {
    const errors: ValidationError[] = [];
    const warnings: ValidationError[] = [];

    // Validate all sections
    validateDocument(invoice, errors, warnings);
    validateSeller(invoice, errors, warnings);
    validateBuyer(invoice, errors, warnings);
    validatePayment(invoice, errors, warnings);
    validateLineItems(invoice, errors, warnings);
    validateTotals(invoice, errors, warnings);

    return {
        valid: errors.length === 0,
        errors,
        warnings,
    };
}

/**
 * Format validation result as string for logging
 */
export function formatValidationResult(result: ValidationResult): string {
    const lines: string[] = [];

    if (result.valid) {
        lines.push('Validation passed');
    } else {
        lines.push('Validation failed');
    }

    if (result.errors.length > 0) {
        lines.push(`\nErrors (${result.errors.length}):`);
        result.errors.forEach(err => {
            lines.push(`  - [${err.code}] ${err.field}: ${err.message}`);
        });
    }

    if (result.warnings.length > 0) {
        lines.push(`\nWarnings (${result.warnings.length}):`);
        result.warnings.forEach(warn => {
            lines.push(`  - [${warn.code}] ${warn.field}: ${warn.message}`);
        });
    }

    return lines.join('\n');
}
